// run-command-cli-wiring.ts
//
// `avatar autopilot run`'s full action body (P9b) — split out of
// commands/autopilot.ts purely to keep that file ≤200 lines (same reason
// autopilot-control-directive-commands.ts / autopilot-ops-checks-cli-wiring.ts
// were split in earlier phases). This is what closes the gap found at P8
// review: `run` used to stop after `startSupervisor`'s one-time gate +
// bootstrap — the real dispatch loop (`runMissionToIdle` ->
// `runSupervisorUntilIdle`) had no production caller. Now `run` calls
// `runMissionToIdle` directly (it internally calls `startSupervisor` itself
// and always releases the machine lock — callers must NOT call
// `startSupervisor` separately, or the lock would be acquired twice).
import { join } from "node:path";
import type { EffectiveConfigLoadOk } from "./effective-config-loader.js";
import { forceCleanupOrphanLocksOwnedBySelf } from "./force-cleanup-orphan-locks-owned-by-self.js";
import { realLivenessProbe } from "./process-liveness-probe.js";
import { applyInterruptedOutcomeOverride } from "./run-command-interrupted-outcome-override.js";
import { printRunOutcome } from "./run-command-outcome-observability.js";

import { makeRunCommandPromptBuilder } from "./run-command-prompt-builder.js";
import { buildReleaseFlowResumeInputFactory } from "./run-command-release-flow-wiring.js";
import { buildRunCommandSessionDeps, claudeBinOverrideNotice } from "./run-command-session-deps.js";
import { buildRunCommandTransportFactory } from "./run-command-transport-factory.js";
import {
  installRunInterruptHandlers,
  type RunInterruptHandlers,
} from "./run-interrupt-signal-handler.js";
import { DEFAULT_SHUTDOWN_GRACE_SEC } from "./runtime-limits-from-effective-config.js";
import type { RunMissionResult } from "./supervisor-mission-run.js";

export interface RunAutopilotRunCommandInput {
  workspaceRoot: string;
  missionId: string;
  missionHome: string;
  controlRoot: string;
  repoFlag: string | undefined;
  packSourceDirForRun: string;
  machineLockPath: string;
  globalConfigPath: string;
  maxTicks: number | undefined;
  log: { info: (msg: string) => void; warn: (msg: string) => void; error: (msg: string) => void };
}

/**
 * Runs `avatar autopilot run`'s dispatch-to-idle sequence and returns the
 * outcome. Never calls `process.exit` itself — the caller (commands/
 * autopilot.ts) decides exit codes, keeping this function unit-testable.
 */
export async function runAutopilotRunCommand(
  input: RunAutopilotRunCommandInput,
): Promise<RunMissionResult> {
  const missionWorkspace = join(input.missionHome, "workspace");
  const packRoot = join(missionWorkspace, ".claude", "pack");

  const notice = claudeBinOverrideNotice();
  if (notice) input.log.info(notice);

  const supervisorStartedAt = await realLivenessProbe.processStartedAt(process.pid);
  const { sessionDeps, currentWorker } = buildRunCommandSessionDeps({
    controlRoot: input.controlRoot,
    supervisorStartedAt,
  });
  let graceMs = DEFAULT_SHUTDOWN_GRACE_SEC * 1000;
  const interrupt = installRunInterruptHandlers({
    currentWorker,
    currentGraceMs: () => graceMs,
    forceCleanup: () =>
      forceCleanupOrphanLocksOwnedBySelf(input.machineLockPath, input.controlRoot),
    notify: input.log.error,
  });

  try {
    const result = await applyInterruptedOutcomeOverride({
      controlRoot: input.controlRoot,
      isInterrupted: interrupt.isInterrupted,
      log: input.log,
      run: () =>
        runOnce(input, missionWorkspace, packRoot, sessionDeps, interrupt, (loaded) => {
          graceMs = (loaded.config.shutdown_grace_sec ?? DEFAULT_SHUTDOWN_GRACE_SEC) * 1000;
          // Req 3 (D7, C4): the ONLY place `run` learns which §18 keys the
          // user's file declared that v1 never reads — same stream/channel
          // (stdout via log.warn) the banner/header below already use, so
          // an operator sees one ordered log, never a second output mode.
          for (const w of loaded.unusedKeyWarnings) input.log.warn(w);
        }),
    });
    // BUG-1 (dogfood 260925 luồng 3): print the FINAL outcome — after
    // applyInterruptedOutcomeOverride may have rewritten it to "interrupted"
    // — never the raw pre-override result `runOnce` produced. Printing used
    // to happen INSIDE runOnce, before the override ran, so Ctrl-C during a
    // run showed stale outcomes (e.g. "idle") while the journal correctly
    // said "interrupted".
    await printRunOutcome({
      missionId: input.missionId,
      controlRoot: input.controlRoot,
      result,
      log: input.log,
      missionWorkspace,
    });
    return result;
  } finally {
    // CXR-R1: an interrupt's recipe/worker stop finishes before the handler goes.
    await interrupt.drain();
    interrupt.dispose();
  }
}

type RunOnceSessionDeps = ReturnType<typeof buildRunCommandSessionDeps>["sessionDeps"];

async function runOnce(
  input: RunAutopilotRunCommandInput,
  missionWorkspace: string,
  packRoot: string,
  sessionDeps: RunOnceSessionDeps,
  interrupt: Pick<RunInterruptHandlers, "isInterrupted" | "drain">,
  onEffectiveConfigLoaded: (loaded: EffectiveConfigLoadOk) => void,
): Promise<RunMissionResult> {
  const { runMissionToIdle } = await import("./supervisor-mission-run.js");

  const result = await runMissionToIdle({
    missionId: input.missionId,
    workspaceRoot: input.workspaceRoot,
    missionWorkspace,
    sourceRepo: input.repoFlag ?? input.workspaceRoot,
    packSourceDir: input.packSourceDirForRun,
    controlRoot: input.controlRoot,
    machineLockPath: input.machineLockPath,
    globalConfigPath: input.globalConfigPath,
    sessionDeps,
    isInterrupted: interrupt.isInterrupted,
    // CXR-R1: the machine lock is released only after that stop.
    beforeLockRelease: interrupt.drain,
    onEffectiveConfigLoaded,
    // (code-review phase-03 [Medium#1]) business outcomes (approve
    // rejected, abort-confirm hints) — not runtime crashes — so `warn`, not
    // `error`, to keep console output distinguishable from a real crash log
    // line during incident debugging.
    notify: (line) => input.log.warn(line),
    // Deferred: reading pack playbook files is real I/O that must NOT run
    // before the §18 beforeDispatch gate has had a chance to block a `run`
    // outright (a mission whose pack has no workflows/autopilot/ yet, e.g.
    // quiet-hours-blocked before any ticket ever dispatches, must exit on
    // the gate's reason — not on a missing-playbook contract_error that was
    // never going to matter). runMissionToIdle only calls this AFTER the
    // startup gate passes and dispatch is actually about to begin.
    buildPromptFactory: () =>
      makeRunCommandPromptBuilder(packRoot, missionWorkspace, input.controlRoot),
    // Phase 03 (R-15, B19): operator-facing progress (header/▶/■) — stdout via
    // `log.info` even in --non-interactive (D14; the JSON machine contract
    // rides stderr on error, this is a separate line never parsed as JSON).
    progress: (line) => input.log.info(line),
    // The pre-existing repo tree is already committed at base_sha
    // (mission-initializer-worktree.ts's own invariant) — only files
    // `autopilot init` added AFTER worktree creation (.avatar/tickets/,
    // .gitignore entries) are actually uncommitted, so staging "." at
    // bootstrap picks up exactly those, matching commit-owner.ts's own
    // "exactly the paths init left uncommitted" contract without having to
    // enumerate them by name (there is no fixed list in production — only
    // test fixtures hardcode one).
    bootstrapManifestPaths: ["."],
    maxTicks: input.maxTicks,
    // Bug G fix: `config` isn't known at this layer (resolved inside
    // runMissionToIdle after startSupervisor) — releaseFlowResumeInputFactory
    // itself takes `config` as a parameter (not pre-bound), so this only
    // constructs the real deps (git/PAT/host) lazily, on the actual
    // effective-config runMissionToIdle already loaded. No-ops (via
    // maybeResumeReleaseFlowAfterDone) when config.release is absent.
    // P19c ("0 caller" #10): before this line `mattermostTransport` had ZERO
    // hits in this file, so `avatar autopilot run` was ALWAYS local-only and
    // no directive filed through chat — approve G3 included — could reach
    // the supervisor. Same deferred-factory shape as the release flow below:
    // credentials/tunables/approver identity all live in effective-config,
    // which only exists inside runMissionToIdle. Returns undefined (=>
    // local-only, today's behaviour) whenever chat is not set up.
    // Req 2 (phase 05, C4): banner is the ONLY place production learns
    // whether Mattermost is wired for THIS run — printed via `onBanner`
    // (stdout, `log.info`) at the moment the factory actually resolves,
    // which is before `runSupervisorUntilIdle`'s own header line (factory
    // runs first inside runMissionToIdle — see supervisor-mission-run.ts).
    mattermostTransportFactory: buildRunCommandTransportFactory({
      workspaceRoot: input.workspaceRoot,
      controlRoot: input.controlRoot,
      missionId: input.missionId,
      onBanner: input.log.info,
    }),
    releaseFlowResumeInputFactory: (config) =>
      buildReleaseFlowResumeInputFactory({
        repoDir: missionWorkspace,
        controlRoot: input.controlRoot,
        missionId: input.missionId,
        config,
      })(),
  });

  return result;
}
